import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import { Spinner, t } from "@/components/ui";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/features/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/features/auth/VerifyEmailPage"));
const OAuthCallbackPage = lazy(() => import("@/features/auth/OAuthCallbackPage"));
const AcceptInvitePage = lazy(() => import("@/features/auth/AcceptInvitePage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const AgendaPage = lazy(() => import("@/features/agenda/AgendaPage"));
const SpeakersPage = lazy(() => import("@/features/speakers/SpeakersPage"));
const MessagesPage = lazy(() => import("@/features/messages/MessagesPage"));
const EventsPage = lazy(() => import("@/features/events/EventsPage"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const ProfilePage = lazy(() => import("@/features/profile/ProfilePage"));
const SpeakerPortalPage = lazy(() => import("@/features/portal/SpeakerPortalPage"));
const RunOfShowPage = lazy(() => import("@/features/runofshow/RunOfShowPage"));
const PublicAgendaPage = lazy(() => import("@/features/agenda/PublicAgendaPage"));
const SessionFeedbackPage = lazy(() => import("@/features/feedback/SessionFeedbackPage"));
const FeedbackRollupPage = lazy(() => import("@/features/feedback/FeedbackRollupPage"));
const PrivacyPolicyPage = lazy(() => import("@/features/legal/PrivacyPolicyPage"));
const LandingPage = lazy(() => import("@/features/landing/LandingPage"));

function RouteFallback() {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: t.bg,
    }}>
      <Spinner size={28} />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        position="top-right"
        richColors
        closeButton
        theme="dark"
        toastOptions={{
          style: {
            background: "#1f2130",
            border: "1px solid #2a2d3f",
            color: "#fff",
            fontFamily: "'Geist', 'Inter', system-ui, sans-serif",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.32)",
          },
        }}
      />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route path="/portal/:token" element={<SpeakerPortalPage />} />
          <Route path="/agenda/:slug" element={<PublicAgendaPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/feedback/session/:sessionId" element={<SessionFeedbackPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/" element={<RootGate />} />

          {/* Protected — wrapped in AppShell */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="agenda" element={<AgendaPage />} />
              <Route path="runofshow" element={<RunOfShowPage />} />
              <Route path="speakers" element={<SpeakersPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="feedback" element={<FeedbackRollupPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function RootGate() {
  const [hydrated, setHydrated] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  useEffect(() => {
    setHydrated(true);
  }, []);

  if (!hydrated) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}
